import {
  AgentConversation,
  AgentMessage,
  AgentTask,
} from '../../../services/api/aiAgentApi/aiAgent';

type BadgeColor = `gray` | `red` | `yellow` | `green` | `blue` | `orange` | `purple`;

export interface StatusMeta {
  label: string;
  color: BadgeColor;
}

export function parseConversationSummary(raw?: string | null): { summary: string; decisionSteps: string[] } {
  if (!raw) {
    return {
      summary: ``,
      decisionSteps: [],
    };
  }

  try {
    const parsed = JSON.parse(raw);

    if (parsed && typeof parsed === `object` && typeof (parsed as { summary?: unknown }).summary === `string`) {
      return {
        summary: (parsed as { summary: string }).summary,
        decisionSteps: Array.isArray((parsed as { decisionSteps?: unknown }).decisionSteps) ?
          (parsed as { decisionSteps: unknown[] }).decisionSteps.filter((step: unknown): step is string => typeof step === `string`) :
          [],
      };
    }
  }
  catch {
    // Legacy conversation summaries were plain text.
  }

  return {
    summary: raw,
    decisionSteps: [],
  };
}

function toTitleCase(value: string) {
  return value
    .replace(/_/g, ` `)
    .replace(/\b\w/g, char => char.toUpperCase());
}

function asString(value: unknown): string | undefined {
  if (typeof value === `string` && value.trim()) {
    return value.trim();
  }

  if (typeof value === `number` && Number.isFinite(value)) {
    return String(value);
  }

  return undefined;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(item => asString(item))
    .filter((item): item is string => Boolean(item));
}

function compactText(value: string, maxLength = 120): string {
  const normalized = value.replace(/\s+/g, ` `).trim();

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, Math.max(maxLength - 1, 0)).trimEnd()}...`;
}

function asRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === `object`) {
    return value as Record<string, unknown>;
  }

  return {};
}

function readRecipients(args: Record<string, unknown>, output: Record<string, unknown>): string | undefined {
  const resolvedRecipientEmails = asStringArray(args._resolvedRecipientEmails);
  const recipients = asStringArray(output.recipients);
  const recipient = asString(output.recipient);
  const overrideEmail = asString(args.overrideEmail);
  const overrideNumber = asString(args.overrideNumber);

  const values = [
    ...resolvedRecipientEmails,
    recipient,
    ...recipients,
    overrideEmail,
    overrideNumber,
  ].filter((item): item is string => Boolean(item));

  if (!values.length) {
    return undefined;
  }

  return values.join(`, `);
}

function readResolvedRecipientEmails(args: Record<string, unknown>): string | undefined {
  const resolvedRecipientEmails = asStringArray(args._resolvedRecipientEmails);

  if (resolvedRecipientEmails.length) {
    return resolvedRecipientEmails.join(`, `);
  }

  return asString(args.overrideEmail);
}

function linesToDetail(lines: Array<string | undefined>): string | undefined {
  const detailLines = lines.filter((line): line is string => Boolean(line));

  if (!detailLines.length) {
    return undefined;
  }

  return detailLines.join(`\n`);
}

export function getConversationStatusMeta(status: AgentConversation[`status`]): StatusMeta {
  switch (status) {
  case `open`:
    return { label: `Open`, color: `blue` };
  case `awaiting_approval`:
    return { label: `Needs approval`, color: `orange` };
  case `awaiting_input`:
    return { label: `Waiting on customer`, color: `yellow` };
  case `needs_human`:
    return { label: `Needs a human`, color: `red` };
  case `resolved`:
    return { label: `Resolved`, color: `green` };
  case `closed`:
    return { label: `Closed`, color: `gray` };
  default:
    return { label: toTitleCase(status), color: `gray` };
  }
}

export function getTaskApprovalMeta(status: string): StatusMeta {
  switch (status) {
  case `pending`:
    return { label: `Awaiting your approval`, color: `orange` };
  case `approved`:
  case `auto_approved`:
    return { label: `Approved`, color: `green` };
  case `rejected`:
    return { label: `Declined`, color: `red` };
  default:
    return { label: toTitleCase(status), color: `gray` };
  }
}

const SEND_TOOLS = new Set([
  `send_email_to_customer`,
  `send_sms_to_customer`,
  `resend_invoice_email`,
  `send_statement`,
]);

function readOutputRecipients(output: Record<string, unknown>): string | undefined {
  const recipients = asStringArray(output.recipients);
  const recipient = asString(output.recipient);
  const values = [
    recipient,
    ...recipients,
  ].filter((item): item is string => Boolean(item));

  if (!values.length) {
    return undefined;
  }

  return values.join(`, `);
}

export interface SendToolCardPreview {
  recipient?: string;
  snippet?: string;
}

export function getSendToolCardPreview(message: AgentMessage, task?: AgentTask): SendToolCardPreview | undefined {
  if (!task || task.execution_status !== `executed` || !SEND_TOOLS.has(message.tool_name || ``)) {
    return undefined;
  }

  const args = asRecord(message.arguments);
  const output = asRecord(message.output);
  const recipient = readOutputRecipients(output);
  const rawSnippet = asString(args.body) || asString(args.content);
  const snippet = rawSnippet ? compactText(rawSnippet, 160) : undefined;

  if (!recipient && !snippet) {
    return undefined;
  }

  return {
    recipient,
    snippet,
  };
}

export function getToolStatusMeta(task?: AgentTask): StatusMeta | undefined {
  if (!task) {
    return undefined;
  }

  if (task.execution_status === `failed`) {
    return { label: `Failed`, color: `red` };
  }

  if (task.approval_status === `rejected`) {
    return { label: `Declined`, color: `red` };
  }

  if (task.approval_status === `pending`) {
    return { label: `Awaiting approval`, color: `orange` };
  }

  if (task.execution_status === `executed`) {
    return SEND_TOOLS.has(task.tool_name) ?
      { label: `Sent`, color: `green` } :
      { label: `Done`, color: `green` };
  }

  return undefined;
}

const TOOL_LABELS: Record<string, string> = {
  get_contact_by_id: `Look up latest contact details & documents`,
  get_other_contact_conversations: `Look up the contact's other conversations`,
  get_conversation_detail: `Read another conversation in full`,
  send_email_to_customer: `Email the customer`,
  send_sms_to_customer: `Text the customer`,
  resend_invoice_email: `Resend the invoice email`,
  send_statement: `Send an account statement`,
  create_invoice_note: `Add a note to an invoice`,
  set_expected_payment_date: `Set an expected payment date`,
  create_contact_note: `Add a note to the contact`,
  update_contact_person: `Update a contact person`,
  escalate_to_staff: `Escalate to a human`,
  resolve_conversation: `Resolve the conversation`,
};

export function friendlyToolLabel(toolName: string | null | undefined): string {
  if (!toolName) {
    return `Action`;
  }

  return TOOL_LABELS[toolName] || toTitleCase(toolName);
}

export interface TaskActionDescription {
  title: string;
  detail?: string;
}

function describeContactPersonChanges(args: Record<string, unknown>): { header: string; lines: string[] } {
  const contactPersonId = asString(args.contactPersonId);
  const firstName = asString(args.firstName);
  const lastName = asString(args.lastName);
  const email = asString(args.email);
  const mobileNumber = asString(args.mobileNumber);
  const mobileCountryCode = asString(args.mobileCountryCode);
  const includeInNotifications = typeof args.includeInNotifications === `boolean` ? args.includeInNotifications : undefined;
  const name = [firstName, lastName].filter((value): value is string => Boolean(value)).join(` `);
  const mobile = [mobileCountryCode, mobileNumber].filter((value): value is string => Boolean(value)).join(` `).trim();

  const isUpdate = Boolean(contactPersonId);

  return {
    header: isUpdate ? `Update contact person` : `Add a new contact person`,
    lines: [
      isUpdate ? `Action: Edit existing contact person` : `Action: Add new contact person`,
      name ? `Name: ${name}` : ``,
      email ? `Email: ${email}` : ``,
      mobile ? `Mobile: ${mobile}` : ``,
      includeInNotifications === undefined ? `` : `Receives notifications: ${includeInNotifications ? `Yes` : `No`}`,
    ],
  };
}

export function describeTaskAction(task: AgentTask): TaskActionDescription {
  const args = asRecord(task.args);

  switch (task.tool_name) {
  case `send_email_to_customer`:
    return {
      title: `Email the customer`,
      detail: linesToDetail([
        `To: ${readResolvedRecipientEmails(args) || `Default contact email`}`,
        asString(args.subject) ? `Subject: ${asString(args.subject)}` : undefined,
        asString(args.body) ? `Body: ${asString(args.body)}` : undefined,
      ]),
    };
  case `send_sms_to_customer`:
    return {
      title: `Text the customer`,
      detail: linesToDetail([
        `To: ${asString(args.overrideNumber) || `Default contact number`}`,
        asString(args.body) ? `Body: ${asString(args.body)}` : undefined,
      ]),
    };
  case `resend_invoice_email`:
    return {
      title: `Resend the invoice email`,
      detail: linesToDetail([
        `To: ${readResolvedRecipientEmails(args) || `Selected contact email on file`}`,
      ]),
    };
  case `send_statement`:
    return {
      title: `Send an account statement`,
      detail: linesToDetail([
        asString(args.statementType) ? `Statement type: ${asString(args.statementType)}` : undefined,
      ]),
    };
  case `create_invoice_note`:
    return {
      title: `Add a note to an invoice`,
      detail: asString(args.content),
    };
  case `create_contact_note`:
    return {
      title: `Add a note to the contact`,
      detail: asString(args.content),
    };
  case `update_contact_person`: {
    const { header, lines } = describeContactPersonChanges(args);

    return {
      title: header,
      detail: linesToDetail([ ...lines ]),
    };
  }
  case `set_expected_payment_date`:
    return {
      title: `Set an expected payment date`,
      detail: linesToDetail([
        asString(args.expectedPaymentDate) ? `Expected payment date: ${asString(args.expectedPaymentDate)}` : undefined,
        asString(args.content) ? `Note: ${asString(args.content)}` : undefined,
      ]),
    };
  case `escalate_to_staff`:
    return {
      title: `Escalate to a human`,
      detail: asString(args.reason),
    };
  case `resolve_conversation`:
    return { title: `Mark this conversation as resolved` };
  default:
    return { title: friendlyToolLabel(task.tool_name) };
  }
}

export function friendlyRole(role: string | null): string {
  switch (role) {
  case `user`:
    return `Customer`;
  case `staff`:
    return `Staff`;
  case `assistant`:
    return `Agent`;
  case `system`:
    return `System`;
  default:
    return role ? toTitleCase(role) : `Message`;
  }
}

export function friendlyTriggerType(type: string): string {
  switch (type) {
  case `inbound_sms`:
    return `Customer replied by SMS`;
  case `inbound_email`:
    return `Customer replied by email`;
  case `manual`:
    return `Started manually`;
  case `resume`:
    return `Resumed after approval`;
  case `resolve_check`:
    return `Re-checked if resolved`;
  default:
    return toTitleCase(type);
  }
}

export type ToolActivityMedium = `email` | `sms` | `statement`;

export interface ToolActivityDescription {
  label: string;
  medium?: ToolActivityMedium;
  summary?: string;
  actionId?: string;
}

export function mediumBadgeLabel(medium: ToolActivityMedium): string {
  switch (medium) {
  case `email`:
    return `Email`;
  case `sms`:
    return `SMS`;
  case `statement`:
    return `Statement`;
  }
}

export function describeToolActivity(message: AgentMessage): ToolActivityDescription {
  const args = asRecord(message.arguments);
  const output = asRecord(message.output);
  const channel = asString(output.channel);
  const medium = channel === `email` || channel === `sms` || channel === `statement` ?
    channel :
    undefined;
  const recipients = readRecipients(args, output);
  const subject = asString(args.subject);
  const body = asString(args.body);
  const content = asString(args.content);
  const statementType = asString(output.statementType) || asString(args.statementType);
  const reason = asString(args.reason);
  const expectedPaymentDate = asString(args.expectedPaymentDate);
  const actionId = asString(output.actionId);

  switch (message.tool_name) {
  case `send_email_to_customer`: {
    const summary = linesToDetail([
      recipients ? `To ${recipients}` : undefined,
      subject ? `Subject: ${compactText(subject, 100)}` : undefined,
      body ? compactText(body, 120) : undefined,
    ]);

    return {
      label: friendlyToolLabel(message.tool_name),
      medium: medium || `email`,
      summary,
      actionId,
    };
  }
  case `send_sms_to_customer`:
    return {
      label: friendlyToolLabel(message.tool_name),
      medium: medium || `sms`,
      summary: linesToDetail([
        recipients ? `To ${recipients}` : undefined,
        body ? compactText(body, 120) : undefined,
      ]),
      actionId,
    };
  case `send_statement`:
    return {
      label: friendlyToolLabel(message.tool_name),
      medium: medium || `statement`,
      summary: linesToDetail([
        recipients ? `To ${recipients}` : undefined,
        statementType ? `Type: ${toTitleCase(statementType)}` : undefined,
      ]),
      actionId,
    };
  case `resend_invoice_email`:
    return {
      label: friendlyToolLabel(message.tool_name),
      medium: medium || `email`,
      summary: linesToDetail([
        recipients ? `To ${recipients}` : undefined,
      ]),
      actionId,
    };
  case `create_invoice_note`:
  case `create_contact_note`:
    return {
      label: friendlyToolLabel(message.tool_name),
      summary: content ? compactText(content, 140) : undefined,
      actionId,
    };
  case `update_contact_person`: {
    const { header, lines } = describeContactPersonChanges(args);

    return {
      label: header,
      summary: linesToDetail([ ...lines ]),
      actionId,
    };
  }
  case `set_expected_payment_date`:
    return {
      label: friendlyToolLabel(message.tool_name),
      summary: linesToDetail([
        expectedPaymentDate ? `Expected payment date: ${expectedPaymentDate}` : undefined,
        content ? compactText(content, 120) : undefined,
      ]),
      actionId,
    };
  case `escalate_to_staff`:
    return {
      label: friendlyToolLabel(message.tool_name),
      summary: reason ? compactText(reason, 140) : undefined,
      actionId,
    };
  default:
    return {
      label: friendlyToolLabel(message.tool_name),
      summary: recipients ? `To ${recipients}` : undefined,
      actionId,
      medium,
    };
  }
}

export function getContactInitials(name?: string | null): string {
  if (!name || !name.trim()) {
    return `?`;
  }

  const parts = name.trim().split(/\s+/).slice(0, 2);

  return parts.map(part => part.charAt(0).toUpperCase()).join(``);
}
